import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { produce } from 'immer';

import { useContainerContext } from '@nextroll/ar-container-context';

import { WorkflowAction } from 'types/roflWorkflowActions';
import { NodeRecordFields, generateInitialMetadata } from 'store/nodeSlice';
import { propertyDefinitionLookup } from 'api/workflowActionsApi';
import { RequestError, UseRequest } from 'hooks/useRequest';
import { ActionConfigValues, ActionMetadata, IPropertyError, IWorkflowActionConfig } from 'types/rofl';
import useAbortController from 'hooks/useAbortController';
import { PropertyType, RecordField } from 'types/roflWorkflowActionProperty';
import { ValidationError } from 'util/errors';

export interface GenerateRecordFieldsConfig {
    currentWorkflowActionConfig?: IWorkflowActionConfig;
    originalWorkflowActionConfig?: IWorkflowActionConfig;
    workflowAction: WorkflowAction | undefined;
    call: UseRequest[0];
    handleUpdateRecordFields: (recordFields: NodeRecordFields) => void;
    handleUpdateWorkflowNode: (config: ActionConfigValues, metadata?: ActionMetadata) => void;
    recordFields?: NodeRecordFields;
    showWorkflowActionPanelProperties: boolean;
    handleRequestError: (requestError: RequestError) => boolean;
    propertyErrors: Record<string, IPropertyError> | undefined;
}

/**
 * This hook handles obtaining the definitions of record type properties. It handles dependencies for those properties
 * and determines if a new request must be executed.
 * For example, is a property depends on "record_type" to determine which record we need to get definitions for,
 * it will retrieve the definitions as soon as those dependencies are satisfied and when switching between record
 * types.
 */
function useGenerateRecordFields({
    workflowAction,
    handleUpdateRecordFields,
    handleUpdateWorkflowNode,
    recordFields,
    showWorkflowActionPanelProperties,
    currentWorkflowActionConfig,
    originalWorkflowActionConfig,
    call,
    handleRequestError,
    propertyErrors
}: GenerateRecordFieldsConfig) {
    const getSignal = useAbortController();
    const currentActionConfig = currentWorkflowActionConfig?.config;
    const currentMetadata = currentWorkflowActionConfig?.metadata;
    const originalMetadata = originalWorkflowActionConfig?.metadata;
    const workflowActionEid = workflowAction?.eid;
    const { advertisable: { eid: advertisableEid = '' } = {} } = useContainerContext();
    const previousRecordFields = useRef(recordFields);
    if (!previousRecordFields.current) {
        previousRecordFields.current = recordFields;
    }
    let recordFieldsChanged = false;
    if (recordFields && !_.isEqual(recordFields, previousRecordFields.current)) {
        const prevRecordFieldMap = { ...previousRecordFields.current };
        const currentRecordFieldMap = { ...recordFields };

        // Differences in just the order of fields should not trigger the deletion of record fields
        // Sort the 'fields' property and then recompare prevRecordField and currentRecordField
        for (const recordField of Object.keys(prevRecordFieldMap)) {
            const prevRecordField = { ...prevRecordFieldMap[recordField] };
            const currentRecordField = { ...currentRecordFieldMap[recordField] };

            prevRecordField.fields = _.sortBy(prevRecordField.fields, 'name');
            currentRecordField.fields = _.sortBy(currentRecordField.fields, 'name');
            if (!_.isEqual(prevRecordField, currentRecordField)) {
                recordFieldsChanged = true;
            }
        }
    }

    const propertyDependencies = useMemo(
        () => workflowAction?.properties
            .filter(
                property => property.type === PropertyType.RECORD
            ).reduce(
                (propertyDependenciesAccumulator: Record<string, IWorkflowActionConfig['config']>, property) => {
                    propertyDependenciesAccumulator[property.name] = (property.depends_on || []).reduce(
                        (dependencies: IWorkflowActionConfig['config'], dependency) => {
                            if (currentActionConfig?.[dependency]) {
                                dependencies![dependency] = currentActionConfig[dependency];
                            }
                            return dependencies;
                        }, {}
                    );
                    return propertyDependenciesAccumulator;
                }, {}
            ), [currentActionConfig, workflowAction]
    );
    const propertiesToUpdate = useMemo(() => workflowAction?.properties.filter(
        property => {
            if (property.type !== PropertyType.RECORD) {
                return false;
            }
            const dependencies = propertyDependencies?.[property.name] || {};
            // Dependencies are not met, so we can't obtain a definition.
            if (Object.keys(dependencies).length < (property.depends_on || []).length) {
                return false;
            }
            // If we don't have a definition at all or if dependencies changed, we should retrieve a new
            // definition.
            return !recordFields?.[property.name]
                || !_.isEqual(dependencies, recordFields?.[property.name].dependencies);
        }
    ), [recordFields, propertyDependencies, workflowAction]);

    useEffect(() => {
        const fetchRecordDefinitions = async () => {
            if (!propertyDependencies || !propertiesToUpdate || propertiesToUpdate.length === 0
                || !workflowActionEid || !showWorkflowActionPanelProperties) {
                return;
            }
            const fetchDefinitionsAbortSignal = getSignal();
            const newRecordFields: NodeRecordFields = (await Promise.all(
                propertiesToUpdate.map(
                    async property => {
                        const dependencies = propertyDependencies[property.name]!;
                        const apiRecordFields = await propertyDefinitionLookup({
                            advertisableEid,
                            workflowActionEid,
                            propertyName: property.name,
                            properties: dependencies,
                            abortSignal: fetchDefinitionsAbortSignal,
                            call,
                            onError: handleRequestError
                        });
                        return [property.name, apiRecordFields] as [string, RecordField[]];
                    }
                )))
                .reduce(
                    (recordFieldsAccumulator: NodeRecordFields, [property, fields]) => {
                        const dependencies = propertyDependencies[property]!;
                        recordFieldsAccumulator[property] = {
                            fields,
                            dependencies,
                        };
                        return recordFieldsAccumulator;
                    }, {}
                );
            if (!fetchDefinitionsAbortSignal.aborted) {
                handleUpdateRecordFields({ ...newRecordFields });
            }
        };
        fetchRecordDefinitions();
    }, [advertisableEid,
        call,
        getSignal,
        handleUpdateRecordFields,
        propertiesToUpdate,
        propertyDependencies,
        workflowActionEid,
        showWorkflowActionPanelProperties,
        handleRequestError]);

    // sort the recordFields based off the property metadata from the server, if it exists
    useEffect(() => {
        let alreadyAddedMissingFields = false;
        if (recordFields && originalMetadata) {
            const newRecordFields = produce(recordFields, draftRecordFields => {
                // this block determines if we have any metadata fields with no corresponding record. If thats the case
                // we create a "fake" record field so that something can render to the screen later.
                if (propertyErrors) {
                    for (const propertyError of Object.values(propertyErrors)) {
                        if (propertyError.error === ValidationError.FIELD_MISSING) {
                            const propertyName = propertyError.property_name;
                            const metadataFields = originalMetadata?.properties[propertyName]?.fields;
                            const recordFieldNames
                                = draftRecordFields[propertyName].fields.map(recordField => recordField.name);
                            if (
                                metadataFields && propertyError.record_field
                                && !recordFieldNames.includes(propertyError.record_field)
                            ) {
                                const fieldDefinition = metadataFields[propertyError.record_field]?.definition;
                                if (fieldDefinition) {
                                    draftRecordFields[propertyName].fields.push(
                                        {
                                            ...fieldDefinition,
                                            missing: true,
                                        }
                                    );
                                }
                            } else {
                                alreadyAddedMissingFields = true;
                            }
                        }
                    }
                }

                for (const propertyName of Object.keys(draftRecordFields)) {
                    const metadataFields = originalMetadata?.properties[propertyName]?.fields;
                    if (metadataFields) {
                        // sort the metadata fields by their index property,
                        // and create an array of the field names
                        const sortedFields = Object.entries(metadataFields)
                            .sort(([, a], [, b]) => a.index - b.index)
                            .map(sortedField => sortedField[0]);

                        // inefficient, but sort the existing record fields based off of the position
                        // from the sortedFields above
                        for (let i = 0; i < sortedFields.length; i++) {
                            const fieldIdx = draftRecordFields[propertyName]
                                .fields?.findIndex((draftRecordField => draftRecordField.name === sortedFields[i]));
                            const field = draftRecordFields[propertyName].fields?.[fieldIdx];
                            draftRecordFields[propertyName].fields?.splice(fieldIdx, 1);
                            draftRecordFields[propertyName].fields?.splice(i, 0, field);
                        }
                    }
                }
            });

            if (!alreadyAddedMissingFields && !_.isEqual(recordFields, newRecordFields)) {
                const recordFieldsForUpdate = { ...newRecordFields };
                previousRecordFields.current = recordFieldsForUpdate;
                handleUpdateRecordFields(recordFieldsForUpdate);
            }
        }
    }, [originalMetadata, recordFields, handleUpdateRecordFields, propertyErrors]);


    // when the record fields change, loop through the old record field property names
    // and clear the old workflow node config and metadata
    useEffect(() => {
        if (previousRecordFields.current && recordFieldsChanged && currentActionConfig) {
            for (const recordFieldName of Object.keys(previousRecordFields.current)) {
                let updateWorkflowValues = false;
                const newWorkflowConfig = produce(currentActionConfig, draftWorkflowConfig => {
                    if (draftWorkflowConfig[recordFieldName] !== undefined) {
                        delete draftWorkflowConfig[recordFieldName];
                        updateWorkflowValues = true;
                    }
                });

                const metadata = currentMetadata || generateInitialMetadata();
                const newMetadata = produce(metadata, draftMetadata => {
                    if (draftMetadata.properties[recordFieldName] !== undefined) {
                        delete draftMetadata.properties[recordFieldName];
                        updateWorkflowValues = true;
                    }
                });
                if (updateWorkflowValues) {
                    handleUpdateWorkflowNode(newWorkflowConfig, newMetadata);
                }
            }
            previousRecordFields.current = recordFields;
        }
    }, [currentActionConfig, currentMetadata, recordFields, handleUpdateWorkflowNode, recordFieldsChanged]);
}

export default useGenerateRecordFields;
